import { Close } from '@fountain/fountain-ui-components';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import {
  ATTRIBUTE_BASED,
  AttributeConditionDraft,
  attributeChoices,
  attributeFor,
  attributeScopeChoices,
  CatalogOption,
  CatalogState,
  COMPARISON_MESSAGES,
  COMPENSATION_BASED,
  CompensationConditionDraft,
  ConditionDraft,
  ConditionError,
  ConditionLogic,
  ConditionScope,
  ConditionType,
  conditionTypesFor,
  DATA_KEY_BASED,
  DataKeyConditionDraft,
  defaultConditionType,
  duplicateSingleUseKeys,
  emptyConditionOfType,
  fieldFor,
  isAttributeSourced,
  isTypeAuthorable,
  isUnary,
  isUsableAmount,
  LOCATION_BASED,
  LocationConditionDraft,
  mintConditionKey,
  OPENING_BASED,
  OpeningConditionDraft,
  OPERATOR_MESSAGES,
  OptionSource,
  POSITION_BASED,
  PositionConditionDraft,
  singleUseTypesInUse,
  UNAVAILABLE_COMPENSATION_UNITS,
  validateCondition,
  withAttributeName,
  withAttributeScope,
  withCompareTo,
  withConditionType,
  withDataKey,
  withJobPayRange,
} from './conditionForm';
import {
  SearchableMultiSelect,
  SearchableMultiSelectOption,
} from './SearchableMultiSelect';
import { ConditionSelect } from './ConditionSelect';
import messages from './messages';
import { useStyles } from './styles';
import { SubjectType } from './types';

export interface ConditionBuilderProps {
  conditions: ConditionDraft[];
  catalog: CatalogState;
  /** Errors are only surfaced once the admin has tried to save. */
  showErrors: boolean;
  onChange: (conditions: ConditionDraft[]) => void;
  /**
   * How this level's conditions combine. Flat across the level — one operator
   * for the whole set, not a nested expression (AXHE-3930).
   */
  logic: ConditionLogic;
  onLogicChange: (logic: ConditionLogic) => void;
  /** Retries the catalog fetch; the builder is unusable without it. */
  onRetryCatalog: () => void;
  /** Distinguishes one level's rows from another's in the DOM. */
  idPrefix: string;
  /**
   * Whether to head the section with "Conditions".
   *
   * On a level card it is the only thing naming what the rows are. In the rule's
   * scope section (AXHE-4467) the radio the rows sit under has already said it —
   * "Offers that meet conditions" — and repeating the word underneath adds a
   * heading level for content that is one choice deep.
   */
  showHeading?: boolean;
  /**
   * Which entity the rule gates. Decides the condition types on offer and the
   * attributes they may name — an opening has no applicant, so it has no pay and
   * no offer-field conditions (AXHE-4383).
   */
  subjectType: SubjectType;
  /**
   * Whether these rows scope the rule or gate one level — `condition_scope` in
   * `Approvals::ConditionsValidator`.
   *
   * Not presentational. A rule's scope has no fail-closed protection, so the
   * server refuses types and attributes there that it accepts on a level, where
   * the same unknown holds the level for a human instead of dropping the rule.
   */
  scope: ConditionScope;
}

/** Which helper text a row's error maps to, so the message lives in one place. */
const ERROR_MESSAGES: Record<ConditionError, keyof typeof messages> = {
  no_data_key: 'conditionNeedsField',
  unknown_data_key: 'conditionFieldUnavailable',
  no_operator: 'conditionNeedsOperator',
  unsupported_operator: 'conditionOperatorUnavailable',
  no_value: 'conditionNeedsValue',
  unknown_value: 'conditionValueUnavailable',
  bad_value: 'conditionValueWrongType',
  no_compare_key: 'conditionNeedsCompareKey',
  unknown_compare_key: 'conditionCompareKeyUnavailable',
  no_attribute: 'conditionNeedsAttribute',
  unknown_attribute: 'conditionAttributeUnavailable',
  unsourced_attribute: 'conditionAttributeUnsourced',
  no_positions: 'conditionNeedsPositions',
  no_locations: 'conditionNeedsLocations',
  no_openings: 'conditionNeedsOpenings',
  type_unavailable: 'conditionPayNotAllowed',
  type_not_allowed: 'conditionTypeNotAllowed',
  no_comparison_operator: 'conditionNeedsOperator',
  no_amount: 'conditionNeedsAmount',
  no_range: 'conditionNeedsRange',
  bad_range: 'conditionBadRange',
  duplicate_single_use: 'conditionDuplicateType',
};

const LOGIC_MESSAGES: Record<ConditionLogic, keyof typeof messages> = {
  and: 'conditionLogicAnd',
  or: 'conditionLogicOr',
};

/** What an admin reads for each condition type. */
const TYPE_MESSAGES: Record<string, keyof typeof messages> = {
  [COMPENSATION_BASED]: 'conditionTypeCompensation',
  [DATA_KEY_BASED]: 'conditionTypeDataKey',
  [ATTRIBUTE_BASED]: 'conditionTypeAttribute',
  [POSITION_BASED]: 'conditionTypePosition',
  [OPENING_BASED]: 'conditionTypeOpening',
  [LOCATION_BASED]: 'conditionTypeLocation',
};

/** What an admin reads for each `SubjectContext` scope. */
const ATTRIBUTE_SCOPE_MESSAGES: Record<string, keyof typeof messages> = {
  opening: 'attributeScopeOpening',
  location: 'attributeScopeLocation',
  position: 'attributeScopePosition',
};

/**
 * Which keyboard and picker the browser gives a typed literal.
 *
 * Everything not in the vocabulary lands on text, which is also where
 * `ValueType.normalize` puts an unrecognised type — so a field the server
 * degraded to string is not offered a numeric spinner here.
 */
const valueInputType = (type?: string | null) => {
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'text';
};

/** A field or an attribute, as far as the value widget needs to read it. */
interface TypedField {
  type?: string | null;
  allowed_values?: string[];
}

/**
 * Whether the value is chosen rather than typed.
 *
 * A boolean has exactly two, so a text box is the wrong control twice over: it
 * accepts input the field cannot hold, and it gives no clue which spelling
 * counts. `ValueType.coerce_boolean` takes `true/false/yes/no/1/0` and returns
 * nil for anything else — and a nil operand makes the comparison false, so
 * "Requires relocation equals Y" would save cleanly and then quietly never
 * gate anything.
 */
const isPickList = (field?: TypedField) =>
  field?.type === 'boolean' || Boolean(field?.allowed_values?.length);

/**
 * The conditions deciding whether a rule or one of its levels applies
 * (AXHE-3929, AXHE-4383).
 *
 * A row starts with the *kind* of thing it is about — the applicant's pay, an
 * offer field, an attribute, a position, an opening, a location — and the
 * controls after it change with that choice. Which kinds are on offer is the
 * server's answer, not this component's: `conditionForm` reads it from the
 * catalog, which derives it from the predicates `ConditionsValidator` itself
 * calls, so the builder cannot present a combination the save then refuses.
 *
 * Every picker is the design system's searchable select — the single-choice ones
 * through `ConditionSelect` (AXHE-4492), the set-membership ones through
 * `SearchableMultiSelect` (AXHE-4757), which adds chips, a per-chip remove and a
 * clear-all.
 *
 * The locations picker files its options under group headers, joining each
 * option's `group` to the `location_groups` source. A header picks the locations
 * under it in bulk and stores their names; scoping a rule to the group itself is
 * the picker beside it, and the two are not interchangeable — a group keeps
 * matching locations added to it later, a set of names does not.
 *
 * Nothing is editable until the catalog is `ready`. An empty field picker with
 * no explanation reads as "there is nothing to gate on here", which is the one
 * thing a failed fetch does not mean — and on an existing rule it would render
 * live conditions as blank selects on a compliance surface.
 */
export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  conditions,
  catalog,
  showErrors,
  onChange,
  logic,
  onLogicChange,
  onRetryCatalog,
  idPrefix,
  showHeading = true,
  subjectType,
  scope,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  /**
   * The field picker's options, built once for the whole set rather than per row.
   *
   * The list reads nothing from a row, and it is the long one — the account's
   * whole data-key catalog. Rebuilding it inside the loop handed every picker a
   * new array on each render, and react-select compares `options` by identity.
   *
   * A field with no key is dropped rather than offered: nothing can be stored for
   * it, `fieldFor` can never match it back, and as an option it is a blank line
   * that takes the row from unconfigured to unsaveable.
   */
  /**
   * The three entity pickers' options, mapped once for the whole set.
   *
   * Same reason `fieldOptions` below is memoised, and it bites harder here:
   * react-select compares `options` by identity and takes a `setState` when it
   * changes, and the panel re-renders on every keystroke in the row's typed value
   * input. Mapped inside the picker this handed all three a new array each time.
   *
   * The `ready` check is inside the memo rather than around it. This component
   * returns early while the catalog is loading or failed, so a hook placed after
   * those returns runs on some renders and not others — which React 16 answers by
   * throwing "Rendered more hooks than during the previous render" the moment a
   * retry succeeds under an open panel.
   */
  const entityOptions = React.useMemo(() => {
    const forSource = (source: OptionSource) =>
      (catalog.status === 'ready' ? catalog.catalog.options[source] ?? [] : []).map(
        (option: CatalogOption) => ({
          value: option.value ?? '',
          // A funnel's title is nullable, and the catalog ships the null rather
          // than inventing a placeholder on the server, so the id is what is
          // left. Falsy rather than nullish: a title of `''` is as unusable as a
          // missing one, and `id_options` only calls `presence` on its own side.
          label: option.label || undefined,
          // Only a location carries one, and only the locations picker reads it.
          // Carried on every source because all four are mapped through here and
          // a picker with no headers to show never looks.
          group: option.group,
        }),
      );

    return {
      positions: forSource('positions'),
      locations: forSource('locations'),
      openings: forSource('openings'),
      location_groups: forSource('location_groups'),
    };
  }, [catalog]);

  const fieldOptions = React.useMemo(
    () =>
      catalog.status === 'ready'
        ? catalog.catalog.fields.flatMap(field =>
            field.key
              ? [{ value: field.key, label: field.label ?? field.key }]
              : [],
          )
        : [],
    [catalog],
  );

  const update = (key: string, next: ConditionDraft) =>
    onChange(conditions.map(row => (row.key === key ? next : row)));

  const label = (message: keyof typeof messages) =>
    intl.formatMessage(messages[message]);

  // One word per operator, whatever the field's type. The design's own
  // `OPERATORS_BY_TYPE` words a date field's `greater_than` as "after" and
  // `less_than` as "before"; `copywriting-voice` asks for the opposite — "one word
  // for one idea, pick a term and reuse it, a synonym reads as a second thing" —
  // and the Trigger column on the rule list reads from this same map, so a
  // per-type split would word one condition two ways across two surfaces.
  const operatorLabel = (operator: string) => {
    const message = OPERATOR_MESSAGES[operator];
    return message ? label(message) : operator;
  };

  const comparisonLabel = (operator: string) => {
    const message = COMPARISON_MESSAGES[operator];
    return message ? label(message) : operator;
  };

  const typeLabel = (type: string) => {
    const message = TYPE_MESSAGES[type];
    return message ? label(message) : type;
  };

  /**
   * The row's message, for the control it is about.
   *
   * A row has at most one error — `validateCondition` returns the first thing
   * wrong with it — so each control asks whether that one is its own. Naming the
   * errors a control owns beside the control keeps the pairing readable where
   * repeating the comparison inside both an `error` and a `helperText` had the
   * two drift apart.
   */
  const errorFor = (
    error: ConditionError | undefined,
    ...owned: ConditionError[]
  ) => (error && owned.includes(error) ? label(ERROR_MESSAGES[error]) : undefined);

  /** The three states a typed or picked literal can be refused in. */
  const valueError = (error?: ConditionError) =>
    errorFor(error, 'no_value', 'unknown_value', 'bad_value');

  const logicLabel = (value: ConditionLogic) => label(LOGIC_MESSAGES[value]);

  // Hoisted out of the row loop: it is one value per render, and computing it at
  // each connector let the click and its accessible name drift apart.
  const otherLogic: ConditionLogic = logic === 'and' ? 'or' : 'and';

  /**
   * The options behind a picked value.
   *
   * Booleans are stored as the strings `ValueType.coerce_boolean` reads, and
   * shown as Yes/No — an admin gating on a checkbox question is asking whether
   * it was ticked, not what its serialised form is.
   *
   * A stored value that is not among them is carried in as its own option. This
   * builder wrote a *free text* box for booleans until AXHE-4473, and
   * `coerce_boolean` accepts `yes/no/1/0/t/f` too — so those conditions exist,
   * are valid, and evaluate correctly. Without this the select would find no
   * matching option and render blank, while `validateCondition` passed the row
   * for being non-empty: a live gate that reads as unconfigured and flags
   * nothing. The same holds for an `allowed_values` list the account has since
   * edited.
   */
  const valueOptions = (
    field?: TypedField,
    stored?: string,
  ): { value: string; label?: string }[] => {
    const options =
      field?.type === 'boolean'
        ? [
            { value: 'true', label: intl.formatMessage(globalMessages.yes) },
            { value: 'false', label: intl.formatMessage(globalMessages.no) },
          ]
        : (field?.allowed_values ?? []).map(allowed => ({ value: allowed }));

    if (stored && !options.some(option => option.value === stored)) {
      return [...options, { value: stored }];
    }
    return options;
  };

  // Uppercased, at 12px in gray700 — the caption treatment the design settled on
  // for every section of a level card (AXHE-5024). No role on this theme carries
  // uppercase, so it is stated in `sectionCaption` rather than looked up, which
  // is why it needed a decision from the designer rather than a token. `h5` is
  // the 12px role; `component` keeps the outline at the `<h4>` this heading sits
  // at, under the panel's `<h3>`.
  const heading = showHeading && (
    <Typography variant="h5" component="h4" className={styles.sectionCaption}>
      <FormattedMessage {...messages.conditionsLabel} />
    </Typography>
  );

  if (catalog.status === 'loading') {
    return (
      <div className={styles.conditions}>
        {heading}
        <div className={styles.conditionsPlaceholder}>
          <CircularProgress
            size={20}
            aria-label={intl.formatMessage(messages.conditionsLoading)}
          />
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage {...messages.conditionsLoading} />
          </Typography>
        </div>
      </div>
    );
  }

  if (catalog.status === 'error') {
    return (
      <div className={styles.conditions}>
        {heading}
        {/* The rest of the panel still works — a level's approvers can be edited
            and saved, and `toPayload` re-emits these conditions as they were.
            Only the builder is unavailable, and it says so with a way back. */}
        <div className={styles.error} role="alert">
          <Typography variant="body1">
            <FormattedMessage {...messages.conditionsLoadError} />
          </Typography>
          <Button variant="outlined" onClick={onRetryCatalog}>
            <FormattedMessage {...messages.retry} />
          </Button>
        </div>
      </div>
    );
  }

  const { catalog: fieldCatalog } = catalog;
  const typeEntries = conditionTypesFor(fieldCatalog, subjectType);
  const duplicateKeys = duplicateSingleUseKeys(fieldCatalog, conditions);
  const nextType = defaultConditionType(fieldCatalog, subjectType, scope, conditions);

  /**
   * Why a type option is greyed, said once under the whole set.
   *
   * The pay note is selected by the reason *token*, not by "some entry has a
   * reason". `unavailable_reason` is an open string in the contract, so a future
   * type blocked for an unrelated reason would otherwise be explained by a
   * sentence about pay. An unrecognised reason says nothing rather than something
   * false.
   *
   * These are rendered in flow rather than referenced from the select. A note
   * under the whole set explains one option, so describing the input with it
   * would attach the reason to every option and to none in particular. So a
   * screen-reader user meets the greyed option without the reason, and the notes
   * below are what a sighted admin reads. Closing that properly needs the reason
   * in the option's own label, which is a copy change the design has not made.
   */
  const showPayNote = typeEntries.some(
    entry => entry.unavailable_reason === UNAVAILABLE_COMPENSATION_UNITS,
  );
  // The other reason, and a property of this set rather than of the account:
  // another row has already spent a type the server allows once.
  //
  // Read off the same call `typeSelect` greys an option with, rather than from
  // "some row holds a single-use type". `singleUseTypesInUse` takes `exceptKey`,
  // so a row never spends a type against itself — one lone Position row greys
  // nothing anywhere, and asking the simpler question printed a note about a
  // disabled option the admin could not find.
  const showSingleUseNote = conditions.some(
    condition =>
      singleUseTypesInUse(fieldCatalog, conditions, condition.key).length > 0,
  );

  /**
   * One set-membership picker.
   *
   * Only the entity sources are capped server-side, which is why only they
   * declare a truncated list. An account's data keys and attributes are emitted
   * whole, so those pickers have no partial state to announce — and the note
   * matters more here than anywhere else on the row, because the control is
   * searchable: a search over a thousand of an account's four thousand locations
   * has to say so, or a missing location reads as a location that does not exist.
   *
   * `groups` files the options under headers, and only locations pass any. A
   * header is a bulk selector over the locations below it, not a scope of its
   * own — that is the `location_groups` picker beside it, whose values the
   * condition stores and keeps matching as the group's membership changes.
   */
  const entitySelect = (
    condition: ConditionDraft,
    source: OptionSource,
    messageKey: keyof typeof messages,
    values: string[],
    onValues: (next: string[]) => void,
    error?: string,
    groups?: SearchableMultiSelectOption[],
  ) => {
    const options = entityOptions[source];
    const truncated = fieldCatalog.truncatedOptions.includes(source);

    return (
      <SearchableMultiSelect
        id={`${idPrefix}-${source}-${condition.key}`}
        className={styles.conditionEntity}
        // Shares its line with the type picker, and on a location row with a
        // second one of these.
        dense
        label={label(messageKey)}
        values={values}
        options={options}
        groups={groups}
        onChange={onValues}
        error={error}
        note={
          truncated
            ? intl.formatMessage(messages.conditionOptionsTruncated, {
                count: options.length,
              })
            : undefined
        }
      />
    );
  };

  /** The type picker, which every row starts with. */
  const typeSelect = (condition: ConditionDraft, error?: ConditionError) => {
    // Withheld rather than offered and refused: two pay conditions, or two
    // position conditions, in one set fails the whole rule on save
    // (`ConditionsValidator#single_use_errors`).
    const spent = singleUseTypesInUse(fieldCatalog, conditions, condition.key);
    // A stored row whose type the catalog does not list at all — an older rule, or
    // a subject the server has since narrowed. A select with no matching option
    // renders blank, which is the one thing this builder must never do to a live
    // condition, so the row's own type is carried in.
    const entries = typeEntries.some(entry => entry.type === condition.type)
      ? typeEntries
      : [...typeEntries, { type: condition.type, scopes: [] }];

    return (
      <ConditionSelect
        id={`${idPrefix}-type-${condition.key}`}
        className={styles.conditionType}
        label={label('conditionTypeLabel')}
        value={condition.type}
        options={entries.map(entry => ({
          value: entry.type ?? '',
          label: typeLabel(entry.type ?? ''),
          // Disabled, not hidden, when the server says it cannot be authored
          // yet: an admin looking for pay gating needs to see that it exists and
          // read why below. A type already spent by another row is disabled for
          // the same reason — the alternative is a save the server refuses.
          //
          // This reads against the letter of `constraint-anti-pattern-guard`'s
          // "an action unavailable due to product capability is not displayed",
          // and the conflict is named rather than complied with silently. That
          // rule is aimed at a permanent exclusion; pay gating is a dated
          // platform gap (AXHE-4052) on a control an admin is looking for, so
          // hiding it answers "where is pay gating?" with silence. A type that
          // can never apply to this subject is genuinely absent — see
          // `Approvals::ConditionCatalog`.
          //
          // The row's own type is never disabled, or a stored condition could not
          // be changed out of the state that makes its rule unsaveable.
          isDisabled:
            entry.type !== condition.type &&
            (!isTypeAuthorable(entry, scope) || spent.includes(entry.type ?? '')),
        }))}
        onChange={type =>
          update(condition.key, withConditionType(condition, type as ConditionType))
        }
        error={errorFor(
          error,
          'duplicate_single_use',
          'type_unavailable',
          'type_not_allowed',
        )}
      />
    );
  };

  /** An offer field (data key) compared to a literal or to another field. */
  const dataKeyControls = (
    condition: DataKeyConditionDraft,
    error?: ConditionError,
  ) => {
    const field = fieldFor(fieldCatalog, condition.dataKeyName);
    // Only the fields the server says are comparable with this one. Empty is
    // a real answer — a lone date field has nothing to compare against.
    const comparable = field?.comparable_keys ?? [];
    const unary = isUnary(fieldCatalog, condition.operator);

    return {
      controls: (
        <>
          <ConditionSelect
            id={`${idPrefix}-field-${condition.key}`}
            className={styles.conditionField}
            label={label('conditionFieldLabel')}
            value={condition.dataKeyName}
            options={fieldOptions}
            onChange={dataKeyName =>
              update(condition.key, withDataKey(condition, dataKeyName, fieldCatalog))
            }
            error={errorFor(error, 'no_data_key', 'unknown_data_key')}
          />

          {/* Operators come from the chosen field's type, so this stays
              empty until a field is picked rather than offering every
              operator in the vocabulary. */}
          <ConditionSelect
            id={`${idPrefix}-operator-${condition.key}`}
            className={styles.conditionOperator}
            label={label('conditionOperatorLabel')}
            value={condition.operator}
            options={(field?.operators ?? []).map(operator => ({
              value: operator,
              label: operatorLabel(operator),
            }))}
            onChange={operator => update(condition.key, { ...condition, operator })}
            error={errorFor(error, 'no_operator', 'unsupported_operator')}
          />

          {!unary && condition.compareTo === 'dataKey' && (
            <ConditionSelect
              id={`${idPrefix}-compare-${condition.key}`}
              className={styles.conditionCompareKey}
              label={label('conditionCompareKeyLabel')}
              value={condition.compareKey}
              options={comparable.map(key => ({
                value: key,
                label: fieldFor(fieldCatalog, key)?.label ?? key,
              }))}
              onChange={compareKey =>
                update(condition.key, { ...condition, compareKey })
              }
              error={errorFor(error, 'no_compare_key', 'unknown_compare_key')}
            />
          )}

          {/* A value that is chosen rather than typed. Split from the typed
              input below rather than switching one control's `select` flag: the
              two are different components now, and an account's option list is
              as long and as unsearchable as the field catalog is. */}
          {!unary && condition.compareTo === 'value' && isPickList(field) && (
            <ConditionSelect
              id={`${idPrefix}-value-${condition.key}`}
              className={styles.conditionValue}
              label={label('conditionValueLabel')}
              value={condition.value}
              options={valueOptions(field, condition.value)}
              onChange={value => update(condition.key, { ...condition, value })}
              error={valueError(error)}
            />
          )}

          {!unary && condition.compareTo === 'value' && !isPickList(field) && (
            <TextField
              id={`${idPrefix}-value-${condition.key}`}
              className={clsx(styles.conditionValue, styles.conditionTypedValue)}
              label={label('conditionValueLabel')}
              value={condition.value}
              onChange={event =>
                update(condition.key, { ...condition, value: event.target.value })
              }
              error={Boolean(valueError(error))}
              helperText={valueError(error)}
              type={valueInputType(field?.type)}
              InputLabelProps={field?.type === 'date' ? { shrink: true } : undefined}
            />
          )}
        </>
      ),
      // Under the value it replaces, rather than above the row: it changes what
      // the right-hand side *is*, so it reads as a modifier of that control. A
      // unary operator tests for existence, so there is no right-hand side and
      // the checkbox would be meaningless.
      modifier: !unary && (
        <FormControlLabel
          control={
            <Checkbox
              checked={condition.compareTo === 'dataKey'}
              onChange={event =>
                update(
                  condition.key,
                  withCompareTo(condition, event.target.checked ? 'dataKey' : 'value'),
                )
              }
              inputProps={{ 'aria-label': label('compareToFieldToggle') }}
            />
          }
          label={label('compareToFieldToggle')}
        />
      ),
      // Says why the compare-with picker would have nothing to offer, before the
      // admin ticks the box and finds it empty.
      note: !unary && field && comparable.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage {...messages.noComparableFields} />
        </Typography>
      ),
    };
  };

  /** An attribute, read off the opening, its location or its position. */
  const attributeControls = (
    condition: AttributeConditionDraft,
    error?: ConditionError,
  ) => {
    const scopeOptions = attributeScopeChoices(fieldCatalog, subjectType, scope);
    const choices = attributeChoices(
      fieldCatalog,
      subjectType,
      scope,
      condition.attrScope,
    );
    const attribute = attributeFor(fieldCatalog, subjectType, condition.attrName);
    const unary = isUnary(fieldCatalog, condition.operator);
    const attributeError = errorFor(
      error,
      'no_attribute',
      'unknown_attribute',
      'unsourced_attribute',
    );

    return {
      controls: (
        <>
          {/* Offered whenever the server names more than one readable source. An
              opening rule reads its own attributes and nothing else, so the
              control would be a single option that changes nothing. */}
          {scopeOptions.length > 1 && (
            <ConditionSelect
              id={`${idPrefix}-attrscope-${condition.key}`}
              className={styles.conditionAttributeScope}
              label={label('conditionAttributeScopeLabel')}
              value={condition.attrScope}
              options={scopeOptions.map(option => ({
                value: option,
                label: ATTRIBUTE_SCOPE_MESSAGES[option]
                  ? label(ATTRIBUTE_SCOPE_MESSAGES[option])
                  : option,
              }))}
              onChange={attrScope =>
                update(condition.key, withAttributeScope(condition, attrScope))
              }
            />
          )}

          <ConditionSelect
            id={`${idPrefix}-attribute-${condition.key}`}
            className={styles.conditionField}
            label={label('conditionAttributeLabel')}
            value={condition.attrName}
            options={choices.map(option => ({
              value: option.key ?? '',
              label: option.label ?? option.key ?? '',
            }))}
            onChange={attrName =>
              update(condition.key, withAttributeName(condition, attrName))
            }
            error={attributeError}
          />

          {/* Withheld until the attribute resolves, as the design's row does: its
              type decides both the operators and the value's widget, so before
              then neither control has anything to offer. */}
          {attribute && (
            <ConditionSelect
              id={`${idPrefix}-attroperator-${condition.key}`}
              className={styles.conditionOperator}
              label={label('conditionOperatorLabel')}
              value={condition.operator}
              options={(attribute.operators ?? []).map(operator => ({
                value: operator,
                label: operatorLabel(operator),
              }))}
              onChange={operator => update(condition.key, { ...condition, operator })}
              error={errorFor(error, 'no_operator', 'unsupported_operator')}
            />
          )}

          {!unary && isPickList(attribute) && (
            <ConditionSelect
              id={`${idPrefix}-attrvalue-${condition.key}`}
              className={styles.conditionValue}
              label={label('conditionValueLabel')}
              value={condition.value}
              options={valueOptions(attribute, condition.value)}
              onChange={value => update(condition.key, { ...condition, value })}
              error={valueError(error)}
            />
          )}

          {attribute && !unary && !isPickList(attribute) && (
            <TextField
              id={`${idPrefix}-attrvalue-${condition.key}`}
              className={clsx(styles.conditionValue, styles.conditionTypedValue)}
              label={label('conditionValueLabel')}
              value={condition.value}
              onChange={event =>
                update(condition.key, { ...condition, value: event.target.value })
              }
              error={Boolean(valueError(error))}
              helperText={valueError(error)}
              type={valueInputType(attribute.type)}
              InputLabelProps={
                attribute.type === 'date' ? { shrink: true } : undefined
              }
            />
          )}
        </>
      ),
      modifier: false,
      // Only on a level, and only once the attribute is chosen: on a rule's scope
      // an unsourced attribute is refused outright and the error above says so.
      // Here it saves — the level is held for a human rather than skipped — so the
      // over-gate is stated rather than left to be discovered in production.
      note:
        scope === 'level' &&
        Boolean(condition.attrName) &&
        Boolean(attribute) &&
        !isAttributeSourced(fieldCatalog, condition.attrScope, condition.attrName) && (
          <Typography variant="body2" className={styles.inlineWarning} role="status">
            <FormattedMessage
              {...messages.conditionAttributeHoldsLevel}
              values={{ subject: subjectType }}
            />
          </Typography>
        ),
    };
  };

  /** The applicant's pay, against a figure or the opening's posted range. */
  const compensationControls = (
    condition: CompensationConditionDraft,
    error?: ConditionError,
  ) => {
    // Disabled rather than withheld, which is what Design Requirements §4 asks
    // for ("while on, the amount fields are disabled/greyed") and what the panel
    // already does one section up for the "All offers" radio. It is also the more
    // useful of the two: the amounts a row already holds are exactly what the
    // condition falls back to when the box is unticked, so hiding them hides the
    // consequence of the click.
    const usesRange = condition.useJobPayRange;
    const isRange = condition.comparisonOperator === 'outside_of';

    /**
     * Which box carries the message.
     *
     * `no_range` is raised for the pair — either bound failing produces the one
     * token — so the render has to work out which is at fault, or the red border
     * lands on the field that was already right. `bad_range` is different: both
     * bounds are usable and only their order is wrong, and the copy names the
     * minimum as the one to change, so it goes there alone.
     */
    const minAtFault =
      error === 'bad_range' ||
      (error === 'no_range' && !isUsableAmount(condition.rangeMin));
    const maxAtFault = error === 'no_range' && !isUsableAmount(condition.rangeMax);

    // No currency symbol on either input. An offer's amount carries neither a
    // currency nor a pay period (AXHE-4063), so a "$" would state a unit this
    // surface cannot know — the same reason `triggerSummary` prints the figure
    // bare. The design's mock shows one.
    const amountField = (
      suffix: string,
      messageKey: keyof typeof messages,
      value: string,
      onValue: (next: string) => void,
      showError: boolean,
    ) => (
      <TextField
        id={`${idPrefix}-${suffix}-${condition.key}`}
        className={clsx(styles.conditionValue, styles.conditionTypedValue)}
        label={label(messageKey)}
        value={value}
        onChange={event => onValue(event.target.value)}
        error={showError}
        helperText={showError && error ? label(ERROR_MESSAGES[error]) : undefined}
        disabled={usesRange}
        type="number"
      />
    );

    return {
      controls: (
        <>
          <ConditionSelect
            id={`${idPrefix}-comparison-${condition.key}`}
            className={styles.conditionOperator}
            label={label('conditionOperatorLabel')}
            value={condition.comparisonOperator}
            options={fieldCatalog.comparisonOperators.map(operator => ({
              value: operator,
              label: comparisonLabel(operator),
            }))}
            onChange={comparisonOperator =>
              update(condition.key, { ...condition, comparisonOperator })
            }
            error={errorFor(error, 'no_comparison_operator')}
          />

          {isRange && (
            <>
              {amountField(
                'rangemin',
                'conditionMinAmountLabel',
                condition.rangeMin,
                rangeMin => update(condition.key, { ...condition, rangeMin }),
                minAtFault,
              )}
              {amountField(
                'rangemax',
                'conditionMaxAmountLabel',
                condition.rangeMax,
                rangeMax => update(condition.key, { ...condition, rangeMax }),
                maxAtFault,
              )}
            </>
          )}

          {!isRange &&
            amountField(
              'threshold',
              'conditionAmountLabel',
              condition.threshold,
              threshold => update(condition.key, { ...condition, threshold }),
              error === 'no_amount',
            )}
        </>
      ),
      modifier: (
        <FormControlLabel
          control={
            <Checkbox
              checked={condition.useJobPayRange}
              onChange={event =>
                update(condition.key, withJobPayRange(condition, event.target.checked))
              }
              inputProps={{ 'aria-label': label('useJobPayRangeToggle') }}
            />
          }
          label={label('useJobPayRangeToggle')}
        />
      ),
      note: false,
    };
  };

  const positionControls = (
    condition: PositionConditionDraft,
    error?: ConditionError,
  ) => ({
    controls: entitySelect(
      condition,
      'positions',
      'conditionPositionsLabel',
      condition.positions,
      positions => update(condition.key, { ...condition, positions }),
      errorFor(error, 'no_positions'),
    ),
    modifier: false,
    note: false,
  });

  const openingControls = (
    condition: OpeningConditionDraft,
    error?: ConditionError,
  ) => ({
    controls: entitySelect(
      condition,
      'openings',
      'conditionOpeningsLabel',
      condition.openings,
      openings => update(condition.key, { ...condition, openings }),
      errorFor(error, 'no_openings'),
    ),
    modifier: false,
    note: false,
  });

  /**
   * Two pickers, because `location_based` holds two sets and the engine ORs them:
   * named locations, and whole location groups.
   *
   * They are keyed differently and that is not cosmetic — `locations` stores the
   * name it matches `SubjectContext#location` against, `locationGroups` stores the
   * group's `external_id`. `Approvals::Condition#location_groups` carries why the
   * two sides diverge; the catalog ships each list already keyed correctly, so
   * neither picker converts anything.
   *
   * `no_locations` is one error for the row — the validator refuses only when
   * *both* sets are empty — so it is rendered against the first picker alone.
   * Passing it to both would print the same sentence twice for a single fault,
   * and a screen-reader user would meet it once per control.
   *
   * The group list is handed to both, for two different jobs: the second picker
   * chooses from it, and the first files its locations under it. A header there
   * picks that group's locations in bulk and stores their names — it is not a
   * shortcut to the picker beside it, which is why it writes nothing to
   * `locationGroups`.
   */
  const locationControls = (
    condition: LocationConditionDraft,
    error?: ConditionError,
  ) => ({
    controls: (
      <>
        {entitySelect(
          condition,
          'locations',
          'conditionLocationsLabel',
          condition.locations,
          locations => update(condition.key, { ...condition, locations }),
          errorFor(error, 'no_locations'),
          entityOptions.location_groups,
        )}
        {entitySelect(
          condition,
          'location_groups',
          'conditionLocationGroupsLabel',
          condition.locationGroups,
          locationGroups =>
            update(condition.key, { ...condition, locationGroups }),
        )}
      </>
    ),
    modifier: false,
    note: false,
  });

  const controlsFor = (condition: ConditionDraft, error?: ConditionError) => {
    switch (condition.type) {
      case ATTRIBUTE_BASED:
        return attributeControls(condition, error);
      case POSITION_BASED:
        return positionControls(condition, error);
      case OPENING_BASED:
        return openingControls(condition, error);
      case LOCATION_BASED:
        return locationControls(condition, error);
      case COMPENSATION_BASED:
        return compensationControls(condition, error);
      default:
        return dataKeyControls(condition, error);
    }
  };

  return (
    <div className={styles.conditions}>
      {heading}

      {conditions.map((condition, index) => {
        // The set-level rule wins over the row's own: a duplicate single-use type
        // is the reason the save is refused, and the row's other complaints are
        // moot until the admin changes or removes it.
        const error = showErrors
          ? duplicateKeys.has(condition.key)
            ? ('duplicate_single_use' as ConditionError)
            : validateCondition(condition, fieldCatalog, { subjectType, scope })
          : undefined;
        const { controls, modifier, note } = controlsFor(condition, error);

        return (
          // Keyed by type as well as by row. `withConditionType` keeps the key —
          // it is an edit to the row, not a replacement — but the controls after
          // the type select are different fields of the same element type, so
          // React would update them in place and leave a select's own internal
          // state attached to a control that now means something else.
          <React.Fragment key={`${condition.key}-${condition.type}`}>
            {/* Between adjacent rows only. Every pill in the level shows the
                same word and toggles the same flag, because the level's
                conditions share one operator — that flatness is the deliberate
                simplification in AXHE-3930, not an omission. */}
            {index > 0 && (
              <Chip
                className={styles.connector}
                size="small"
                variant="outlined"
                clickable
                label={logicLabel(logic)}
                onClick={() => onLogicChange(otherLogic)}
                aria-label={intl.formatMessage(messages.conditionLogicToggle, {
                  current: logicLabel(logic),
                  other: logicLabel(otherLogic),
                })}
              />
            )}

            <div className={styles.conditionRow} data-testid="condition-row">
              <div className={styles.conditionLine}>
                <div className={styles.conditionControls}>
                  {typeSelect(condition, error)}
                  {controls}
                </div>

                {/* An icon, not a `×` text node: a glyph has no icon box, so the
                    button measured 17px square — under the minimum pointer
                    target, and it could only be aligned by guessing at the
                    input's own top margin.

                    Outside `conditionControls` rather than wrapping with the
                    fields (AXHE-5139): it needs to take a fixed slice off
                    every wrapped line, not compete for space on whichever one
                    it happens to fall on.

                    Numbered by the row's place in the set: the draft key is a
                    React identity, and reading it out is the system language
                    `constraint-anti-pattern-guard` prohibits. */}
                <IconButton
                  className={styles.conditionRemove}
                  size="small"
                  aria-label={intl.formatMessage(messages.removeCondition, {
                    number: index + 1,
                  })}
                  onClick={() =>
                    onChange(conditions.filter(row => row.key !== condition.key))
                  }
                >
                  <Close className={styles.removeIcon} />
                </IconButton>
              </div>

              {modifier}
              {note}
            </div>
          </React.Fragment>
        );
      })}

      {/* The key is minted against the rows that exist, not from their count —
          see `mintConditionKey`, where the difference is a condition the admin
          configured being overwritten by the next one they add.

          Withheld when every type this subject allows here is either unavailable
          or already spent by a single-use row, because there is nothing a new row
          could be. */}
      {nextType && (
        <Button
          onClick={() =>
            onChange([
              ...conditions,
              emptyConditionOfType(mintConditionKey(idPrefix, conditions), nextType),
            ])
          }
        >
          <FormattedMessage {...messages.addCondition} />
        </Button>
      )}

      {showPayNote && (
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage {...messages.conditionPayUnavailable} />
        </Typography>
      )}

      {showSingleUseNote && (
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage {...messages.conditionTypeSingleUse} />
        </Typography>
      )}
    </div>
  );
};
